import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import {
  VALIDATION_SUMMARY,
  VALIDATION_CATEGORIES,
  RELEASE_PACKAGE_INFO
} from "../../data/create-ontology-validate-release.data";
import "./CreateOntologyValidateRelease.scss";

const CreateOntologyValidateRelease: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;

  const handleBack = () => {
    navigate("/ontology-studio/create/build-knowledge-graph", {
      state: { initiativeId }
    });
  };

  const handlePublish = () => {
    navigate("/create-ontology");
  };

  const passedCount = VALIDATION_CATEGORIES.filter(
    (category) => category.status === "PASSED"
  ).length;

  return (
    <div className="create-vr-container">
      <div className="create-vr-page-header">
        <div className="create-vr-title-block">
          <nav className="create-vr-breadcrumb">
            <Link to="/home" className="breadcrumb-link">
              Engineering Information Management
            </Link>
            <span className="breadcrumb-separator">&gt;</span>
            <span className="breadcrumb-current">Validate &amp; Release</span>
          </nav>
          <h1 className="create-vr-title">Validate &amp; Release</h1>
          <div className="create-vr-meta">
            <span className="create-vr-meta__pill">
              Initiative ID{" "}
              <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
            </span>
            <span className="create-vr-meta__pill create-vr-meta__pill--success">
              Validation Status <strong>Passed</strong>
            </span>
          </div>
          <p className="create-vr-description">
            Review validation outcomes and publish the ontology when all checks
            pass.
          </p>
        </div>
        <div className="create-vr-header-actions">
          <button
            type="button"
            className="create-vr-btn create-vr-btn--secondary"
          >
            <DownloadOutlined fontSize="small" />
            Export Report
          </button>
          <button
            type="button"
            className="create-vr-btn create-vr-btn--primary"
          >
            <RefreshOutlined fontSize="small" />
            Run All Validations
          </button>
        </div>
      </div>

      <div className="create-vr-summary-card">
        <div className="create-vr-summary-card__header">
          <div>
            <p className="create-vr-summary-card__title">Validation Summary</p>
            <p className="create-vr-summary-card__subtitle">
              All validation categories passed. The ontology is ready for
              release.
            </p>
          </div>
          <span className="create-vr-status-pill">READY FOR RELEASE</span>
        </div>

        <div className="create-vr-kpi-row">
          <div className="create-vr-kpi-card">
            <p className="create-vr-kpi-card__label">Validation Pass Rate</p>
            <p className="create-vr-kpi-card__value">
              {VALIDATION_SUMMARY.passRatePercent}%
            </p>
            <p className="create-vr-kpi-card__caption">
              All validation checks completed successfully.
            </p>
          </div>
          <div className="create-vr-kpi-card">
            <p className="create-vr-kpi-card__label">Validation Results</p>
            <p className="create-vr-kpi-card__value">
              {VALIDATION_SUMMARY.unresolvedResults}
            </p>
            <p className="create-vr-kpi-card__caption">
              No unresolved validation issues remain.
            </p>
          </div>
          <div className="create-vr-kpi-card">
            <p className="create-vr-kpi-card__label">Validation Categories</p>
            <p className="create-vr-kpi-card__value">
              {VALIDATION_SUMMARY.categoriesCount}
            </p>
            <p className="create-vr-kpi-card__caption">
              All required checks passed successfully.
            </p>
          </div>
        </div>

        <div className="create-vr-question-row">
          <p className="create-vr-question-row__text">
            Did the ontology pass validation?
          </p>
          <span className="create-vr-status-pill">
            {VALIDATION_SUMMARY.passedValidation ? "YES" : "NO"}
          </span>
        </div>
        <div className="create-vr-question-row">
          <p className="create-vr-question-row__text">
            Can it be published now?
          </p>
          <span className="create-vr-status-pill">
            {VALIDATION_SUMMARY.canPublishNow ? "YES" : "NO"}
          </span>
        </div>

        <div className="create-vr-categories-card">
          <p className="create-vr-categories-card__title">
            Validation Categories
          </p>
          <div className="create-vr-categories-list">
            {VALIDATION_CATEGORIES.map((category) => (
              <div key={category.name} className="create-vr-category-row">
                <span className="create-vr-category-row__name">
                  {category.name}
                </span>
                <span
                  className={`create-vr-status-pill${
                    category.status === "FAILED"
                      ? " create-vr-status-pill--danger"
                      : ""
                  }`}
                >
                  {category.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <button type="button" className="create-vr-link-row">
          <span>View Findings</span>
          <ArrowForwardOutlined fontSize="inherit" />
        </button>
      </div>

      <div className="create-vr-release-card">
        <div className="create-vr-release-card__header">
          <p className="create-vr-release-card__title">
            Release Package Information
          </p>
          <span className="create-vr-status-pill">READY</span>
        </div>
        <div className="create-vr-release-info">
          <div className="create-vr-release-info__row">
            <span>Version</span>
            <strong>{RELEASE_PACKAGE_INFO.version}</strong>
          </div>
          <div className="create-vr-release-info__row">
            <span>Environment</span>
            <strong>{RELEASE_PACKAGE_INFO.environment}</strong>
          </div>
          <div className="create-vr-release-info__row">
            <span>Release Status</span>
            <strong>{RELEASE_PACKAGE_INFO.releaseStatus}</strong>
          </div>
        </div>
        <button type="button" className="create-vr-link-row">
          <span>View Release Notes</span>
          <ArrowForwardOutlined fontSize="inherit" />
        </button>
      </div>

      <div className="create-vr-footer">
        <div className="create-vr-footer__summary">
          <p className="create-vr-footer__title">Final Validation Summary</p>
          <p className="create-vr-footer__text">
            {VALIDATION_SUMMARY.passRatePercent}% validation pass ·{" "}
            {VALIDATION_SUMMARY.unresolvedResults} unresolved issues · Ready for
            release
          </p>
        </div>
        <div className="create-vr-footer__actions">
          <button
            type="button"
            className="create-vr-btn create-vr-btn--secondary"
            onClick={handleBack}
          >
            Back
          </button>
          <button
            type="button"
            className="create-vr-btn create-vr-btn--primary"
            onClick={handlePublish}
          >
            Publish &amp; Release
          </button>
          <span className="create-vr-checks-pill">
            {passedCount} checks passed · {VALIDATION_SUMMARY.unresolvedResults}{" "}
            issues
          </span>
        </div>
      </div>
    </div>
  );
};

export default CreateOntologyValidateRelease;
